<template>
  <div class="comment_item" :class="{ unread: !read }">
    <Avatar
      :rounded="true"
      :color="user.color"
      :src="user.avatarUrl || user.gravatarUrl"
      :alt="user.shortName"
      :initials="user.shortName"
    />
    <div class="right">
      <div class="top">
        <div class="name">{{ user.fullName }}</div>
        <div class="timestamp">{{ elapsedTime }}</div>
        <div
          v-if="isCommentCreator"
          v-click-away="clickAway"
          class="comments-dd"
        >
          <div @click="popoverOpen = !popoverOpen">
            <svg-icon
              :width="'14'"
              :height="'14'"
              fill="#bcbcbc"
              class="dots"
              :class="{ active: popoverOpen }"
              icon="app-icons/dot-menu"
            />
          </div>
          <div v-show="popoverOpen" class="menu">
            <div class="button" @click="onEditComment">Edit</div>
            <div class="button" @click="onDeleteComment">Delete comment</div>
          </div>
        </div>
        <div
          v-if="comment.children"
          v-tooltip="{
            content: comment.resolved ? 'Unresolve thread' : 'Resolve thread',
            placement: 'top',
            delay: delayOptions,
          }"
          class="resolve"
          :class="{ isResolved: comment.resolved }"
          @click="onResolve"
        >
          <svg-icon
            :width="'14'"
            :height="'14'"
            fill="#bcbcbc"
            class="check"
            :class="{ active: popoverOpen }"
            icon="app-icons/check"
          />
        </div>
      </div>
      <rich-text-editor
        ref="content"
        class="content"
        :editable="false"
        :activate-on-double-click="false"
        :extension-options="extensionOptions"
        :content="comment.content"
        @focus="onFocus"
        @update="onUpdate"
        @submit="onSave"
      />
      <div v-if="showButtons" class="buttons">
        <button type="button" class="button cancel" @click="onCancel">
          Cancel
        </button>
        <button
          type="button"
          class="button"
          :disabled="disabled"
          @click="onSave"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { mixin as VueClickAway } from 'vue3-click-away';
import { Avatar } from '@ui/components/avatar';
import RichTextEditor from '../../all/RichText/RichTextEditor.vue';
import SvgIcon from '../../all/SvgIcon.vue';
import { elapsedTimeToString } from '../../../utils.js';
import requests from '../../../vuex/requests.js';

export default {
  components: {
    SvgIcon,
    Avatar,
    RichTextEditor,
  },
  mixins: [VueClickAway],
  props: {
    id: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      popoverOpen: false,
      extensionOptions: {
        hardbreak: false,
        bold: false,
        italic: false,
        underline: false,
        horizontalRule: false,
        customLink: false,
        history: true,
        placeholder: {
          text: 'Reply…',
        },
        dropEventHandler: false,
        customMention: false,
        codeBlockHighlight: false,
        blockquote: false,
      },
      elapsedTime: null,
      focused: false,
      disabled: true,
      delayOptions: { show: 800, hide: 0 },
      interval: 0,
      // storedContent: null,
    };
  },

  computed: {
    ...mapGetters('project/users', ['getParticipant']),
    ...mapGetters('user/projects', { getOpenProject: 'getOpen' }),
    ...mapGetters('user/account', ['getId']),
    ...mapGetters('project/data/comments', {
      getComment: 'getNode',
      getThread: 'getContainerNode',
    }),
    ...mapGetters(['getServerTimeDiff']),
    ...mapGetters('project/config/comments', ['getRead', 'getOpen']),

    /**
     * The participant that has made the comment
     * @returns {Object}
     */
    user() {
      return (
        this.getParticipant(this.comment.userId) || {
          shortName: '?',
          fullName: 'Unknown User',
          avatarUrl: null,
          gravatarUrl: null,
          color: '#363636',
        }
      );
    },

    /**
     * The comment we are displaying
     * @returns {Object}
     */
    comment() {
      return this.getComment(this.id);
    },

    /**
     * Returns if the current user is the one that created the comment
     * @returns {boolean}
     */
    isCommentCreator() {
      return this.getId === this.comment.userId;
    },

    /**
     * Returns if the comment is read
     * @returns {boolean}
     */
    read() {
      return this.getRead(this.id);
    },

    /**
     * Returns if the thread is open
     * @returns {boolean}
     */
    isOpen() {
      const threadId = this.getThread(this.id, 'comments', 'children')?.id;
      return this.getOpen === this.id || this.getOpen === threadId;
    },

    /**
     * Returns whether to show the buttons to save or cancel the comment
     * @returns {boolean}
     */
    showButtons() {
      return this.focused;
    },
  },

  watch: {
    /**
     * Whenever the thread is open update the elapsed time at regular intervals
     */
    isOpen(newVal) {
      if (newVal) {
        this.elapsedTime = this.calculateElapsedTime();
        this.interval = setInterval(() => {
          this.elapsedTime = this.calculateElapsedTime();
        }, 10000);
      } else {
        clearInterval(this.interval);
      }
    },
  },

  mounted() {
    this.elapsedTime = this.calculateElapsedTime();
    // if (this.storedContent) {
    //   console.log('Mounted, there is stored content:', this.storedContent);
    //   this.$refs.content.startEditing('end');
    // }
  },

  /**
   * beforeUnmount vue lifecycle hook: Clear any intervals
   */
  beforeUnmount() {
    clearInterval(this.interval);
    // this.storedContent = this.$refs.content.getContent();
    // console.log('Before destroy, setting storedContent');
  },

  methods: {
    ...mapActions('project/data/comments', {
      deleteComment: 'customDelete',
      updateComment: 'customUpdate',
    }),

    /**
     * Returns the time that has passed since the creation of the comment (server time)
     * @returns Number
     */
    calculateElapsedTime() {
      // When was the comment created (server time)
      const creationTime = new Date(this.comment.createdAt);
      // Get current server time
      const serverTime = new Date(Date.now() - this.getServerTimeDiff);
      const elapsedTime = Math.round(
        (serverTime.getTime() - creationTime.getTime()) / 1000
      );
      return elapsedTimeToString(elapsedTime);
    },

    clickAway() {
      // ENG TO:DO
      // Refactor later
      if (this.popoverOpen) {
        this.popoverOpen = false;
      }
    },

    /**
     * Event handler: the user clicked on the edit option of the dots popover
     */
    onEditComment() {
      this.$refs.content.startEditing();
      this.popoverOpen = false;
    },

    /**
     * Event handler: the user clicked on the edit option of the dots popover
     */
    onDeleteComment() {
      this.deleteComment({ id: this.id, apply: true });
      this.popoverOpen = false;
    },

    /**
     * Event handler: v-popover auto-hide event
     */
    onAutoHide() {
      console.log('Auto-Hide event');
      this.popoverOpen = false;
    },

    /**
     * Event handler: Content rich text editor custom focus event
     */
    onFocus() {
      this.focused = true;
      this.disabled = this.$refs.content.isContentEmpty();
    },

    /**
     * Event handler: Content rich text editor custom focus event
     */
    onUpdate() {
      this.disabled = this.$refs.content.isContentEmpty();
      // if (this.$refs.content.isContentEmpty()) {
      //   this.storedContent = this.$refs.content.getContent();
      // } else {
      //   this.storedContent = null;
      // }
      // console.log('Updated stored content to:', this.storedContent);
    },

    /**
     * Event hanlder: Save button is clicked
     */
    onSave() {
      // Update the comment content to the new one
      const content = this.$refs.content.getContent();
      this.updateComment({ id: this.id, props: { content }, apply: true });
      this.$refs.content.blur();
      this.focused = false;
      this.storedContent = null;
    },

    /**
     * Event handler: Cancel button is clicked
     */
    onCancel() {
      // Reset the editor content to the saved one
      const { content } = this.comment;
      this.$refs.content.setContent(content);
      this.focused = false;
      // this.storedContent = null;
    },

    /**
     * Event handler: Resolve thread button is clicked
     */
    onResolve() {
      // Toggle resolve state
      let { resolved } = this.comment;
      const { content } = this.comment;
      resolved = !resolved;
      this.updateComment({
        id: this.id,
        props: { resolved },
        apply: true,
      }).then(() => {
        if (resolved) {
          requests.projects.resolveCommentNotification({
            hash: this.getOpenProject,
            content,
          });
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
.v-popper__inner {
  .wrapper {
  }
  .comment_item.unread .wrapper:after {
    content: '';
    position: absolute;
    top: 10px;
    left: -20px;
    width: 4px;
    height: 8px;
    background: #fff;
    border-radius: 0 999px 999px 0;
  }
  .comment_item {
    position: relative;
    width: 100%;
    display: inline-flex;
    align-items: flex-start;
    padding: 20px 20px 0 20px;
    &:not(:last-child) {
      // border-bottom: 1px solid rgba(62, 71, 71, 0.75);
    }
    &:hover .right .top .dots {
      // visibility: visible;
    }
    .right {
      margin: 0 0 0 10px;
      width: 260px;
      //overflow: hidden;
      .top {
        margin: 3px 0 10px 0;
        display: flex;
        .name {
          font-size: 12px;
          font-weight: 400;
          margin: 0 10px 0 0;
          color: #828585;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .timestamp {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 12px;
          color: #828585;
          flex: 1 1 auto;
          padding: 0 20px 0 0;
        }
        .dots {
          display: flex;
          opacity: 0.5;
          height: 22px;
          padding: 4px 5px;
          border-radius: 4px;
          cursor: pointer;
          &:hover {
            opacity: 1;
            background: rgba(188, 188, 188, 0.15);
          }
        }
        .resolve {
          margin: 0 0 3px 10px;
          display: flex;
          align-items: center;
          opacity: 0.5;
          cursor: pointer;
          border-radius: 4px;
          padding: 4px 5px;
          &.isResolved {
            opacity: 1;
            // background: #bcbcbc !important;
            :deep(svg path) {
              fill: #db841e !important;
            }
          }

          &:hover {
            opacity: 1;
            background: rgba(188, 188, 188, 0.15);
          }
        }
      }
      :deep(.content p) {
        color: #bcbcbc;
      }
    }
  }
}
.buttons {
  display: flex;
  justify-content: flex-end;
  padding: 20px;
  .button {
    padding: 8px 12px;
    border-radius: 3px;
    display: inline-block;
    color: #fff;
    cursor: pointer;
    font-size: 13px;
    position: relative;
    background: #bf751d;
    border: none;
    min-width: 70px;
    text-align: center;
    margin-left: 10px;
  }
  .button.cancel {
    border: 1px solid #6e797b;
    background: none;
    color: #a7a7a7;
  }

  .button[disabled] {
    background: #40494a !important;
    color: #aaa;
    pointer-events: none;
  }
}
.comments-dd {
  position: relative;
  .menu {
    position: absolute;
    top: 30px;
    right: 0;
    transform: translateX(10px);
    width: 160px;
    background: #2c3233;
    border-radius: 3px;
    border-width: 0;
    box-shadow: 0 1px 10px rgba(0, 0, 0, 0.5);
    z-index: 9;

    .button {
      text-align: left;
      padding: 10px 20px;
      font-size: 14px;
      display: block;
      cursor: pointer;

      &:hover {
        background: #323838;
      }
    }
  }
}
</style>
<style lang="scss">
.v-popper__popper.v-popper--theme-dropdown.v-popper--board-comment-dropdown {
  .v-popper__inner {
    width: 160px;
  }

  .v-popper__arrow-container {
    display: none;
  }

  .menu {
    .button {
      text-align: left;
      padding: 10px 20px;
      font-size: 14px;
      display: block;
      cursor: pointer;

      &:hover {
        background: #323838;
      }
    }
  }
}
</style>
